<template>
  <div class="featured-book-row" v-if="book">
    <div class="book-compact">

      <div class="book-cover">
        <a v-if="book.uid" :href="'/' + book.uid" tabindex="-1">
          <img v-if="book.coverimages?.[0]?.url" :src="book.coverimages[0].url" :alt="book.title" />
          <div v-else class="cover-empty" />
        </a>
      </div>

      <div class="book-details">
        <p class="book-title">{{ book.title }}</p>
        <p class="book-price" v-if="priceLabel">${{ priceLabel }}</p>
        <div class="book-specs" v-if="intro || book.specs">{{ intro || book.specs }}</div>
        <div class="book-actions">
          <button class="add-btn add-btn-sold-out" v-if="book.sold_out === 'true'" disabled>Sold Out</button>
          <button class="add-btn add-btn-sold-out" v-else-if="book.forthcoming === 'true'" disabled>Forthcoming</button>
          <button class="add-btn" v-else-if="priceLabel" @click.prevent.stop="addToCart">Add to Cart</button>
          <a v-if="book.uid" class="about-btn" :href="'/' + book.uid">Read more</a>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
  import { computed } from 'vue'
  import { useStore } from '../KQL.js'
  import { pageFromPath } from '../helpers.js'
  import { useCartStore } from './CartStore.js'

  const kql = useStore();
  const cart = useCartStore();
  const props = defineProps(['uid', 'intro']);

  const book = computed(() => props.uid ? pageFromPath(props.uid, kql.pages) : null);

  const priceLabel = computed(() => {
    const p = book.value?.price;
    if (!p || parseFloat(p) === 0) return null;
    return parseFloat(p).toFixed(2);
  });

  const addToCart = async () => {
    if (!book.value) return;
    const existing = cart.line_items.find(i => i._slug === book.value.uid);
    if (existing) {
      cart.updateQuantity(book.value.uid, existing.quantity + 1);
      cart.isOpen = true;
      cart.loadSession();
    } else {
      await cart.addItem(book.value);
    }
  };
</script>

<style scoped>
  .featured-book-row {
    border-top: 1px solid var(--border);
    padding-top: 2rem;
    padding-bottom: 3rem;
  }

  .book-compact {
    display: flex;
    gap: 3rem;
    align-items: flex-start;
  }

  .book-cover {
    flex-shrink: 0;
    width: 22rem;
    height: 23rem;
    align-self: flex-start;
    overflow: hidden;
  }

  .book-cover a {
    display: block;
    width: 100%;
    height: 100%;
  }

  .book-cover a:hover img { opacity: 0.85; }

  .book-cover img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: contain;
    object-position: center top;
    transition: opacity 0.2s;
  }

  .cover-empty {
    width: 100%;
    aspect-ratio: 346 / 556;
    background: rgba(255, 255, 255, 0.08);
    border-radius: 2px;
  }

  .book-details {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2rem;
    min-width: 0;
    padding-top: 0.25rem;
  }

  .book-title {
    font-family: "Copernicus", serif;
    font-size: 2.5rem;
    font-weight: normal;
    line-height: 1.2;
    color: inherit;
    max-width: 60rem;
    margin: 0;
    transition: opacity 0.2s ease;
  }

  .featured-book-row:hover .book-title {
    opacity: 0.65;
  }

  .book-price {
    font-family: "Maison Neue Demi", sans-serif;
    font-size: 1.5rem;
    color: inherit;
    margin: 0;
  }

  .book-specs {
    font-family: "Copernicus", serif;
    font-size: 1.5rem;
    line-height: 1.45;
    color: inherit;
    max-width: 80rem;
    opacity: 0.85;
  }

  .book-actions {
    display: flex;
    align-items: center;
    gap: 1.5rem;
  }

  .add-btn,
  .about-btn {
    font-family: "Maison Neue Demi", sans-serif;
    font-size: 1.5rem;
    border-radius: 3em;
    padding: 0.55em 1.6em 0.6em;
    margin: 0;
    line-height: 1;
    display: inline-block;
    white-space: nowrap;
    transition: background 0.18s, opacity 0.18s;
    cursor: pointer;
  }

  .add-btn {
    background: var(--btn-solid-bg, #4a2f30);
    color: var(--btn-solid-text, #ebe0e1);
    border: 1px solid var(--btn-solid-bg, #4a2f30);
  }

  .add-btn:hover { opacity: 0.85; }
  .add-btn-sold-out { opacity: 0.45; cursor: default; }

  .about-btn {
    color: inherit;
    background: rgba(235, 224, 225, 0.35);
    border: 1px solid currentColor;
    text-decoration: none;
  }

  .about-btn:hover { background: rgba(235, 224, 225, 0.6); }

  @media (max-width: 600px) {
    .book-compact { flex-direction: column; gap: 2.5rem; align-items: flex-start; }
    .book-cover { width: 100%; max-width: 22rem; align-self: auto; }
    .book-title { font-size: 2.4rem; }
  }
</style>
